/**
 * Capability policy data is generated from backend (source of truth).
 * To regenerate: node _tools/sync-capabilities.js
 * Backend source: backend/src/common/capabilities.ts
 */
import generatedCaps from './generated-capabilities.json';

export const CAPABILITIES = Object.freeze(generatedCaps.CAPABILITIES);
export const ROLE_DEFAULTS = generatedCaps.ROLE_DEFAULTS;
const CAPABILITY_IMPLICATIONS = generatedCaps.CAPABILITY_IMPLICATIONS || {};

const C = CAPABILITIES;

export const CAPABILITY_GROUPS = [
    {
        labelKey: 'capabilities.groups.users',
        capabilities: [{ key: C.USERS_MANAGE, capKey: 'capabilities.caps.users_manage' }],
    },
    {
        labelKey: 'capabilities.groups.venues',
        capabilities: [
            { key: C.VENUES_VIEW, capKey: 'capabilities.caps.venues_view' },
            { key: C.VENUES_MANAGE, capKey: 'capabilities.caps.venues_manage' },
        ],
    },
    {
        labelKey: 'capabilities.groups.shows',
        capabilities: [
            { key: C.SHOWS_VIEW, capKey: 'capabilities.caps.shows_view' },
            { key: C.SHOWS_MANAGE, capKey: 'capabilities.caps.shows_manage' },
            { key: C.TIMESLOTS_VIEW_HIDDEN, capKey: 'capabilities.caps.timeslots_view_hidden' },
        ],
    },
    {
        labelKey: 'capabilities.groups.bookings',
        capabilities: [
            { key: C.BOOKINGS_VIEW, capKey: 'capabilities.caps.bookings_view' },
            { key: C.BOOKINGS_MANAGE, capKey: 'capabilities.caps.bookings_manage' },
            { key: C.BOOKINGS_REFUND, capKey: 'capabilities.caps.bookings_refund' },
            { key: C.BOOKINGS_ALWAYS_REFUND, capKey: 'capabilities.caps.bookings_always_refund' },
            { key: C.BOOKINGS_IMPORT, capKey: 'capabilities.caps.bookings_import' },
            { key: C.BOOKINGS_SCOPE_ALL, capKey: 'capabilities.caps.bookings_scope_all' },
            { key: C.WAITLIST_MANAGE, capKey: 'capabilities.caps.waitlist_manage' },
            { key: C.UPGRADE_MANAGE, capKey: 'capabilities.caps.upgrade_manage' },
            { key: C.SEATING_MODIFY, capKey: 'capabilities.caps.seating_modify' },
            {
                key: C.SEATING_CROSS_TICKET_TYPE,
                capKey: 'capabilities.caps.seating_cross_ticket_type',
            },
            { key: C.SEATING_ASSIGNMENTS, capKey: 'capabilities.caps.seating_assignments' },
            {
                key: C.BOOKINGS_SIMPLIFIED_TICKETS,
                capKey: 'capabilities.caps.bookings_simplified_tickets',
            },
            { key: C.BOOKINGS_MODIFY_PAST, capKey: 'capabilities.caps.bookings_modify_past' },
            { key: C.BOOKINGS_SHOW_STATS, capKey: 'capabilities.caps.bookings_show_stats' },
            { key: C.BOOKINGS_OVERBOOK, capKey: 'capabilities.caps.bookings_overbook' },
            {
                key: C.BOOKINGS_CROSS_SELLING,
                capKey: 'capabilities.caps.bookings_cross_selling',
            },
            {
                key: C.BOOKINGS_CUSTOM_ROLLBACK_TIMES,
                capKey: 'capabilities.caps.bookings_custom_rollback_times',
            },
            { key: C.BOOKINGS_RE_SOURCE, capKey: 'capabilities.caps.bookings_re_source' },
            { key: C.BOOKINGS_CANCEL, capKey: 'capabilities.caps.bookings_cancel' },
            {
                key: C.BOOKINGS_BYPASS_DYNAMIC_PRICING,
                capKey: 'capabilities.caps.bookings_bypass_dynamic_pricing',
            },
        ],
    },
    {
        labelKey: 'capabilities.groups.operations',
        capabilities: [
            { key: C.CATERING_VIEW, capKey: 'capabilities.caps.catering_view' },
            { key: C.CHECKIN_VIEW, capKey: 'capabilities.caps.checkin_view' },
            { key: C.CHECKIN_FINISH, capKey: 'capabilities.caps.checkin_finish' },
        ],
    },
    {
        labelKey: 'capabilities.groups.payment_methods',
        capabilities: [
            { key: C.PAYMENT_METHODS_VIEW, capKey: 'capabilities.caps.payment_methods_view' },
            { key: C.PAYMENT_METHODS_MANAGE, capKey: 'capabilities.caps.payment_methods_manage' },
        ],
    },
    {
        labelKey: 'capabilities.groups.coupons',
        capabilities: [
            { key: C.COUPONS_VIEW, capKey: 'capabilities.caps.coupons_view' },
            { key: C.COUPONS_MANAGE, capKey: 'capabilities.caps.coupons_manage' },
        ],
    },
    {
        labelKey: 'capabilities.groups.integrations',
        capabilities: [
            { key: C.INTEGRATIONS_MANAGE, capKey: 'capabilities.caps.integrations_manage' },
        ],
    },
    {
        labelKey: 'capabilities.groups.commission_groups',
        capabilities: [
            { key: C.COMMISSION_GROUPS_VIEW, capKey: 'capabilities.caps.commission_groups_view' },
            {
                key: C.COMMISSION_GROUPS_MANAGE,
                capKey: 'capabilities.caps.commission_groups_manage',
            },
        ],
    },
    {
        labelKey: 'capabilities.groups.payments',
        capabilities: [
            {
                key: C.PAYMENTS_SPLIT_UNLIMITED,
                capKey: 'capabilities.caps.payments_split_unlimited',
            },
        ],
    },
    {
        labelKey: 'capabilities.groups.invoices',
        capabilities: [{ key: C.INVOICES_VIEW, capKey: 'capabilities.caps.invoices_view' }],
    },
    {
        labelKey: 'capabilities.groups.logs',
        capabilities: [{ key: C.LOGS_VIEW, capKey: 'capabilities.caps.logs_view' }],
    },
    {
        labelKey: 'capabilities.groups.reports',
        capabilities: [
            { key: C.REPORTS_SALES_VIEW, capKey: 'capabilities.caps.reports_sales_view' },
            { key: C.REPORTS_SALES_EXPORT, capKey: 'capabilities.caps.reports_sales_export' },
            {
                key: C.REPORTS_TRANSACTIONS_VIEW,
                capKey: 'capabilities.caps.reports_transactions_view',
            },
            {
                key: C.REPORTS_TRANSACTIONS_EXPORT,
                capKey: 'capabilities.caps.reports_transactions_export',
            },
            { key: C.REPORTS_MANIFEST_VIEW, capKey: 'capabilities.caps.reports_manifest_view' },
            { key: C.REPORTS_MANIFEST_EXPORT, capKey: 'capabilities.caps.reports_manifest_export' },
            { key: C.REPORTS_COMMISSION_VIEW, capKey: 'capabilities.caps.reports_commission_view' },
            {
                key: C.REPORTS_COMMISSION_EXPORT,
                capKey: 'capabilities.caps.reports_commission_export',
            },
            {
                key: C.REPORTS_AFFILIATE_SALES_VIEW,
                capKey: 'capabilities.caps.reports_affiliate_sales_view',
            },
            {
                key: C.REPORTS_AFFILIATE_SALES_EXPORT,
                capKey: 'capabilities.caps.reports_affiliate_sales_export',
            },
            {
                key: C.REPORTS_AFFILIATE_INVOICE_VIEW,
                capKey: 'capabilities.caps.reports_affiliate_invoice_view',
            },
            {
                key: C.REPORTS_AFFILIATE_INVOICE_EXPORT,
                capKey: 'capabilities.caps.reports_affiliate_invoice_export',
            },
            { key: C.REPORTS_PAYOUT_VIEW, capKey: 'capabilities.caps.reports_payout_view' },
            { key: C.REPORTS_PAYOUT_EXPORT, capKey: 'capabilities.caps.reports_payout_export' },
            {
                key: C.REPORTS_BOOKING_FEE_VIEW,
                capKey: 'capabilities.caps.reports_booking_fee_view',
            },
            {
                key: C.REPORTS_BOOKING_FEE_EXPORT,
                capKey: 'capabilities.caps.reports_booking_fee_export',
            },
            {
                key: C.REPORTS_AFFILIATE_CANCELLED_REFUNDED_VIEW,
                capKey: 'capabilities.caps.reports_affiliate_cancelled_refunded_view',
            },
            {
                key: C.REPORTS_AFFILIATE_CANCELLED_REFUNDED_EXPORT,
                capKey: 'capabilities.caps.reports_affiliate_cancelled_refunded_export',
            },
        ],
    },
];

const ALL_CAPABILITY_KEYS = Object.values(CAPABILITIES);

// Returns the cap that should be auto-toggled on when this manage/export cap
// is granted (e.g. bookings.manage → bookings.view, reports.sales_export →
// reports.sales_view). Returns null for view caps and for actions without a
// matching sibling view.
function getSiblingViewCapability(capability) {
    const [resource, action] = capability.split('.');
    if (!action || action === 'view') return null;
    if (action.endsWith('_view')) return null;
    if (action.endsWith('_export')) {
        const compound = `${resource}.${action.replace(/_export$/, '_view')}`;
        if (ALL_CAPABILITY_KEYS.includes(compound)) return compound;
    }
    const sibling = `${resource}.view`;
    return ALL_CAPABILITY_KEYS.includes(sibling) ? sibling : null;
}

export const MANAGE_IMPLIES_VIEW = Object.fromEntries(
    ALL_CAPABILITY_KEYS.flatMap((capability) => {
        const viewCapability = getSiblingViewCapability(capability);
        if (!viewCapability) return [];
        return (CAPABILITY_IMPLICATIONS[capability] || []).includes(viewCapability)
            ? [[capability, viewCapability]]
            : [];
    }),
);

// Direct prerequisite map: capability → caps it requires. Used by the UI to
// flag broken combinations (e.g. seating.cross_ticket_type without
// seating.modify is non-functional even though the backend accepts the row).
export const CAPABILITY_REQUIREMENTS = Object.fromEntries(
    ALL_CAPABILITY_KEYS.map((capability) => [
        capability,
        CAPABILITY_IMPLICATIONS[capability] || [],
    ]),
);

export function getMissingPrerequisites(capability, capsSet) {
    const required = CAPABILITY_REQUIREMENTS[capability] || [];
    return required.filter((dep) => !capsSet.has(dep));
}

export const TEMPLATE_ROLES = Object.keys(ROLE_DEFAULTS);

// ---------------------------------------------------------------------------
// User-management template helpers (mirrors backend/src/common/capabilities.ts)
// ---------------------------------------------------------------------------

const MANAGER_ASSIGNABLE_TEMPLATE_ROLES = [
    'agent',
    'external_agent',
    'guest',
    'caterer',
    'accountant',
];
const ADMIN_ASSIGNABLE_TEMPLATE_ROLES = [
    'admin',
    'manager',
    'agent',
    'external_agent',
    'guest',
    'caterer',
    'accountant',
];

export function getEffectiveCapabilities(caps) {
    const effective = new Set(caps || []);
    const queue = [...effective];

    while (queue.length > 0) {
        const capability = queue.shift();
        const impliedCaps = CAPABILITY_IMPLICATIONS[capability] || [];
        for (const impliedCap of impliedCaps) {
            if (!effective.has(impliedCap)) {
                effective.add(impliedCap);
                queue.push(impliedCap);
            }
        }
    }

    return effective;
}

/**
 * Returns the template roles that the acting user is allowed to assign.
 * Superadmins can assign any template. Other users are classified by the
 * capabilities they hold, then filtered down to templates they can fully delegate.
 */
export function getAssignableTemplates(actorCaps, isSuperAdmin) {
    if (isSuperAdmin) return TEMPLATE_ROLES;

    const effectiveActorCaps = getEffectiveCapabilities(actorCaps);
    if (!effectiveActorCaps.has(C.USERS_MANAGE)) return [];

    const manageableTemplates =
        effectiveActorCaps.has(C.VENUES_MANAGE) && effectiveActorCaps.has(C.SHOWS_MANAGE)
            ? ADMIN_ASSIGNABLE_TEMPLATE_ROLES
            : MANAGER_ASSIGNABLE_TEMPLATE_ROLES;

    return manageableTemplates.filter((role) => {
        const roleCaps = ROLE_DEFAULTS[role] || [];
        return roleCaps.every((cap) => effectiveActorCaps.has(cap));
    });
}

export function arraysMatchAsSet(a, b) {
    if (!a || !b || a.length !== b.length) return false;
    const setB = new Set(b);
    return a.every((item) => setB.has(item));
}

/**
 * Returns a role key for display (e.g. 'admin', 'agent', 'custom') based on capabilities.
 * Uses isSuperAdmin boolean instead of role field.
 */
export function getRoleLabelKey(capabilities, isSuperAdmin) {
    if (isSuperAdmin) return 'superadmin';
    if (capabilities?.length) {
        for (const templateRole of TEMPLATE_ROLES) {
            const templateCaps = ROLE_DEFAULTS[templateRole];
            if (templateCaps && arraysMatchAsSet(capabilities, templateCaps)) {
                return templateRole;
            }
        }
        return 'custom';
    }
    return 'custom';
}

/**
 * Mirrors backend canManageTarget: whether the acting user may manage
 * (edit / reset password / reset 2FA / delete) the target user. Authorization is
 * capability-based, so "Custom" users are handled like any other user: the actor
 * may manage a non-superadmin target when it can delegate every capability the
 * target holds, and - when the target itself holds users.manage - only if the
 * actor is a full venue admin (venues.manage + shows.manage). Superadmins can
 * manage anyone; nobody but a superadmin can manage a superadmin.
 */
export function canManageTarget(actorCaps, actorIsSuperAdmin, targetCaps, targetIsSuperAdmin) {
    if (actorIsSuperAdmin) return true;
    if (targetIsSuperAdmin) return false;

    const actorEffective = getEffectiveCapabilities(actorCaps);
    if (!actorEffective.has(C.USERS_MANAGE)) return false;

    // The actor must be able to delegate every capability the target holds.
    const undelegatable = (targetCaps || []).some((cap) => !actorEffective.has(cap));
    if (undelegatable) return false;

    // A user who can themselves manage users is admin-tier; only full venue
    // admins may manage them.
    if (getEffectiveCapabilities(targetCaps).has(C.USERS_MANAGE)) {
        return actorEffective.has(C.VENUES_MANAGE) && actorEffective.has(C.SHOWS_MANAGE);
    }

    return true;
}
